import React, { useState, useEffect } from 'react';
import { services } from '../../../services/api';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { FiEdit, FiTrash2, FiPlus, FiCheck } from 'react-icons/fi';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { Tabs } from '../../../components/ds';

const TierManagement = () => {
    const { showSnackbar } = useSnackbar();
    //! Phone gets cards, not the table (see the tier list below).
    const { isPhone } = useBreakpoint();
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTier, setEditingTier] = useState(null);
    const [activeTab, setActiveTab] = useState('general');

    const [formData, setFormData] = useState({
        // Identity
        tier_name: '',
        display_name: '',
        description: '',

        // Data Quality
        dq_max_concurrent_brokers: 1,
        dq_multi_broker_enabled: false,
        dq_priority_broker_access: false,
        dq_tick_rate_limit: 1,
        dq_order_depth_level: 0,

        // Strategies
        strat_max_active_strategies: 1,
        strat_max_backtests_per_day: 5,
        strat_live_trading_enabled: false,
        strat_paper_trading_enabled: true,
        strat_custom_indicators_enabled: false,
        strat_max_orders_per_day: 10,

        // Watchlist
        wl_max_watchlist_groups: 3,
        wl_max_items_per_group: 20,
        wl_alerts_enabled: true,

        // Limits
        max_realtime_feeds: 5,
        api_rate_limit: 60
    });

    const fetchTiers = async () => {
        setLoading(true);
        try {
            const response = await services.tiers.list();
            setTiers(response.data.tiers || []);
        } catch (error) {
            console.error('Error fetching tiers:', error);
            showSnackbar('Failed to fetch tiers', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTiers();
    }, []);

    const handleEdit = (tier) => {
        setEditingTier(tier);
        // Flatten nested response structure to form state
        setFormData({
            tier_name: tier.tier_name,
            display_name: tier.display_name || '',
            description: tier.description || '',

            dq_max_concurrent_brokers: tier.data_quality?.max_concurrent_brokers ?? 1,
            dq_multi_broker_enabled: tier.data_quality?.multi_broker_enabled ?? false,
            dq_priority_broker_access: tier.data_quality?.priority_broker_access ?? false,
            dq_tick_rate_limit: tier.data_quality?.tick_rate_limit ?? 1,
            dq_order_depth_level: tier.data_quality?.order_depth_level ?? 0,

            strat_max_active_strategies: tier.strategies?.max_active ?? 1,
            strat_max_backtests_per_day: tier.strategies?.max_backtests_per_day ?? 5,
            strat_live_trading_enabled: tier.strategies?.live_trading_enabled ?? false,
            strat_paper_trading_enabled: tier.strategies?.paper_trading_enabled ?? true,
            strat_custom_indicators_enabled: tier.strategies?.custom_indicators_enabled ?? false,
            strat_max_orders_per_day: tier.strategies?.max_orders_per_day ?? 10,

            wl_max_watchlist_groups: tier.watchlist?.max_groups ?? 3,
            wl_max_items_per_group: tier.watchlist?.max_items_per_group ?? 20,
            wl_alerts_enabled: tier.watchlist?.alerts_enabled ?? true,

            max_realtime_feeds: tier.limits?.max_realtime_feeds ?? 5,
            api_rate_limit: tier.limits?.api_rate_limit ?? 60
        });
        setActiveTab('general');
        setShowModal(true);
    };

    const handleDelete = async (tierId) => {
        if (window.confirm('Are you sure you want to delete this tier?')) {
            try {
                await services.tiers.delete(tierId);
                showSnackbar('Tier deleted successfully', 'success');
                fetchTiers();
            } catch (error) {
                console.error('Error deleting tier:', error);
                showSnackbar('Failed to delete tier', 'error');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingTier) {
                await services.tiers.update(editingTier.id, formData);
                showSnackbar('Tier updated successfully', 'success');
            } else {
                await services.tiers.create(formData);
                showSnackbar('Tier created successfully', 'success');
            }
            setShowModal(false);
            setEditingTier(null);
            resetForm();
            fetchTiers();
        } catch (error) {
            console.error('Error saving tier:', error);
            showSnackbar('Failed to save tier', 'error');
        }
    };

    const resetForm = () => {
        setFormData({
            tier_name: '',
            display_name: '',
            description: '',
            dq_max_concurrent_brokers: 1,
            dq_multi_broker_enabled: false,
            dq_priority_broker_access: false,
            dq_tick_rate_limit: 1,
            dq_order_depth_level: 0,
            strat_max_active_strategies: 1,
            strat_max_backtests_per_day: 5,
            strat_live_trading_enabled: false,
            strat_paper_trading_enabled: true,
            strat_custom_indicators_enabled: false,
            strat_max_orders_per_day: 10,
            wl_max_watchlist_groups: 3,
            wl_max_items_per_group: 20,
            wl_alerts_enabled: true,
            max_realtime_feeds: 5,
            api_rate_limit: 60
        });
    };

    const renderField = (key, label, type = 'text', options = {}) => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-fg-2 mb-1">{label}</label>
            {type === 'checkbox' ? (
                <div className="flex items-center h-10">
                    <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-[var(--divider)] rounded"
                        checked={formData[key]}
                        onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                    />
                    <span className="ml-2 text-sm text-fg-2">Enabled</span>
                </div>
            ) : type === 'number' ? (
                <input
                    type="number"
                    className="ds-input mt-1 block w-full"
                    value={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: parseInt(e.target.value) || 0 })}
                    {...options}
                />
            ) : (
                <input
                    type="text"
                    className="ds-input mt-1 block w-full"
                    value={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    disabled={options.disabled}
                    required={options.required}
                />
            )}
        </div>
    );

    const renderTabs = () => (
        <Tabs
            className="mb-4"
            value={activeTab}
            onChange={setActiveTab}
            tabs={[
                { label: 'General', value: 'general' },
                { label: 'Data Quality', value: 'data_quality' },
                { label: 'Strategies', value: 'strategies' },
                { label: 'Watchlist', value: 'watchlist' },
                { label: 'Limits', value: 'limits' },
            ]}
        />
    );

    return (
        <div className="bg-bg-2 p-4 sm:p-6 rounded-lg shadow">
            {/* Stacks on phone — the heading and Add Tier squashed each other. */}
            <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="ds-h2 text-fg-1">Subscription Tiers</h2>
                <button
                    type="button"
                    onClick={() => { setEditingTier(null); resetForm(); setShowModal(true); }}
                    className="btn btn-primary flex w-full items-center justify-center gap-2 sm:w-auto"
                >
                    <FiPlus /> Add Tier
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : isPhone ? (
                /* PHONE: one card per tier — a 5-column table with the actions in
                   the last column meant panning sideways just to hit Edit/Delete. */
                <div className="flex flex-col gap-3">
                    {tiers.length === 0 && (
                        <div className="rounded-xl border border-[var(--divider)] p-8 text-center text-sm text-fg-2">
                            No tiers defined yet.
                        </div>
                    )}
                    {tiers.map((tier) => (
                        <div key={tier.id} className="rounded-xl border border-[var(--divider)] bg-bg-3/40 p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-fg-1">{tier.display_name || tier.tier_name}</div>
                                    <div className="text-xs text-fg-2">{tier.tier_name.toUpperCase()}</div>
                                </div>
                                <div className="flex shrink-0 gap-3">
                                    <button type="button" aria-label="Edit tier" onClick={() => handleEdit(tier)} className="btn btn-ghost btn-sm">
                                        <FiEdit />
                                    </button>
                                    <button type="button" aria-label="Delete tier" onClick={() => handleDelete(tier.id)} className="btn btn-danger btn-sm">
                                        <FiTrash2 />
                                    </button>
                                </div>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-3 border-t border-[var(--divider)] pt-3 text-sm">
                                <div>
                                    <div className="text-xs text-fg-3">Brokers</div>
                                    <div className="text-fg-1">
                                        {tier.data_quality?.max_concurrent_brokers} Max
                                        {tier.data_quality?.multi_broker_enabled && <span className="ml-2 ds-badge profit sm">Multi</span>}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-fg-3">Strategies</div>
                                    <div className="text-fg-1">{tier.strategies?.max_active} Active</div>
                                </div>
                                <div className="col-span-2">
                                    <div className="text-xs text-fg-3">Trading</div>
                                    <div>
                                        {tier.strategies?.live_trading_enabled ? (
                                            <span className="ds-badge profit sm">Live</span>
                                        ) : (
                                            <span className="ds-badge warn sm">Paper Only</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-bg-3 dark:bg-bg-3">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">Brokers</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">Strategies</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">Trading</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-fg-2 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-bg-2 divide-y divide-gray-200 dark:divide-gray-700">
                            {tiers.map((tier) => (
                                <tr key={tier.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-fg-1">{tier.display_name || tier.tier_name}</div>
                                        <div className="text-xs text-fg-2">{tier.tier_name.toUpperCase()}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                                        {tier.data_quality?.max_concurrent_brokers} Max
                                        {tier.data_quality?.multi_broker_enabled && <span className="ml-2 ds-badge profit sm">Multi</span>}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                                        {tier.strategies?.max_active} Active
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                                        {tier.strategies?.live_trading_enabled ? (
                                            <span className="text-green-600 font-bold">Live</span>
                                        ) : (
                                            <span className="text-yellow-600">Paper Only</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => handleEdit(tier)} className="text-blue-600 hover:text-blue-900 dark:hover:text-blue-400 mr-4">
                                            <FiEdit />
                                        </button>
                                        <button onClick={() => handleDelete(tier.id)} className="text-red-600 hover:text-red-900 dark:hover:text-red-400">
                                            <FiTrash2 />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-bg-2 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h3 className="ds-h3 mb-4 text-fg-1">
                            {editingTier ? `Edit Tier: ${editingTier.tier_name}` : 'New Subscription Tier'}
                        </h3>
                        
                        {renderTabs()}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            
                            {activeTab === 'general' && (
                                <div className="grid grid-cols-1 gap-4">
                                    {renderField('tier_name', 'System Name (ID)', 'text', { disabled: !!editingTier, required: true })}
                                    {renderField('display_name', 'Display Name')}
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-fg-2 mb-1">Description</label>
                                        <textarea
                                            className="ds-input mt-1 block w-full"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            rows={3}
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === 'data_quality' && (
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    {renderField('dq_max_concurrent_brokers', 'Max Concurrent Brokers', 'number')}
                                    {renderField('dq_tick_rate_limit', 'Tick Rate Limit (per sec)', 'number')}
                                    {renderField('dq_order_depth_level', 'Order Depth Level (0-5)', 'number')}
                                    <div className="col-span-1 grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2">
                                        {renderField('dq_multi_broker_enabled', 'Multi-Broker Enabled', 'checkbox')}
                                        {renderField('dq_priority_broker_access', 'Priority Broker Access', 'checkbox')}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'strategies' && (
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    {renderField('strat_max_active_strategies', 'Max Active Strategies', 'number')}
                                    {renderField('strat_max_backtests_per_day', 'Max Backtests (Daily)', 'number')}
                                    {renderField('strat_max_orders_per_day', 'Max Orders (Daily)', 'number')}
                                    <div className="col-span-1 grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2">
                                        {renderField('strat_live_trading_enabled', 'Live Trading Enabled', 'checkbox')}
                                        {renderField('strat_paper_trading_enabled', 'Paper Trading Enabled', 'checkbox')}
                                        {renderField('strat_custom_indicators_enabled', 'Custom Indicators', 'checkbox')}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'watchlist' && (
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    {renderField('wl_max_watchlist_groups', 'Max Groups', 'number')}
                                    {renderField('wl_max_items_per_group', 'Max Items Per Group', 'number')}
                                    {renderField('wl_alerts_enabled', 'Alerts Enabled', 'checkbox')}
                                </div>
                            )}

                            {activeTab === 'limits' && (
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    {renderField('max_realtime_feeds', 'Max Realtime Feeds', 'number')}
                                    {renderField('api_rate_limit', 'API Rate Limit (req/min)', 'number')}
                                </div>
                            )}

                            <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-[var(--divider)]">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="btn btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                >
                                    Save Tier
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TierManagement;
